import * as Cmp from "@impero-com/common/utils/comparison";
import {
    DataExtractor,
    ExtractorExpander,
    PartialData,
} from "@impero-com/common/utils/dataView";
import { t } from "@impero-com/common/utils/i18n";
import { SimpleAndFilter } from "@impero-com/common/utils/indexing";

import { UserTagView } from "api/generated/model";
import { AssignmentRow } from "components/controlAssignment/types";
import { TagListProps, getTagsByCategory } from "components/tag/tagList";
import { TagsData } from "components/tag/types";
import { ControlDataRow } from "org/control/list/controls/types";
import { IdAndName } from "org/pageState/models";

import {
    AbstractTag,
    TagLookupData,
    AbstractTagCategory,
    createCategoryNameLookupFn,
    createTagNameLookupFn,
    getTagMethods,
    TagCategoryExtractorsAndExpanders,
} from "./tag";

const ASSIGNEE_CATEGORIES_ID = "assigneeCategory_";
const ASSIGNEE_CATEGORY_METHODS = getTagMethods(ASSIGNEE_CATEGORIES_ID);
const ASSIGNEE_TAG_CATEGORIES_ID = "assigneeTagCategories";

function buildAssigneeCategoryExtractorsAndExpandersFromTagsAndFilter<
    TAG extends AbstractTag,
    CAT extends AbstractTagCategory,
    ROW extends ControlDataRow,
>(
    allTags: TagLookupData<TAG, CAT>,
    currentFilter: SimpleAndFilter | null
): TagCategoryExtractorsAndExpanders<ROW> {
    const tagNameLookup = createTagNameLookupFn(allTags);

    const extractors: Array<
        DataExtractor<ROW, TagsData, IdAndName<string, string>>
    > = [];
    const expanders: Array<
        ExtractorExpander<TagsData, IdAndName<string, string>>
    > = [];

    const allCategories: AbstractTagCategory[] = allTags.linkedResources.map(
        ({ id, name }) => ({ id, name })
    );
    // In case if one of the categories was removed, but it's still referred in the filter, we want to "resurrect" it by decoding
    // id and name from extractorId, so we can build an extractor and expander.
    if (currentFilter) {
        currentFilter.filters
            .map((f) => f.extractorId)
            .filter(ASSIGNEE_CATEGORY_METHODS.isCategoryExtractorId)
            .forEach((categoryExtractorId) => {
                const { id, name } =
                    ASSIGNEE_CATEGORY_METHODS.extractorIdToAbstractTagCategory(
                        categoryExtractorId
                    );
                const isFound = !!allCategories.find((c) => c.id === id);
                if (!isFound) {
                    allCategories.push({ id, name });
                }
            });
    }

    allCategories.forEach((category) => {
        const extractorId: string =
            ASSIGNEE_CATEGORY_METHODS.abstractTagCategoryToExtractorId({
                id: category.id,
                name: category.name,
            });

        const extractor: DataExtractor<
            ROW,
            TagsData,
            IdAndName<string, string>
        > = {
            id: extractorId,
            label:
                t("Control_Management_AssigneeTagCategory") +
                ": " +
                category.name,
            getValue: (row) => row.assigneeTags,
            // NOTE: comparison does not play any role, because category_ columns are not shown, but only used in search
            compare: (_a, _b) => 0,
            asString: (_row) => {
                throw new Error(
                    `Unexpected call of asString() on ${extractorId} extractor.`
                );
            },
            customFilterTransformer: {
                getValue: (_row) => {
                    throw new Error(
                        `Unexpected call of customFilterTransformer.getValue() on ${extractorId} extractor.`
                    );
                },
                asString: tagNameLookup,
            },
        };

        const expander: ExtractorExpander<
            TagsData,
            IdAndName<string, string>
        > = {
            extractorId,
            expand: (tags: TagsData) => {
                const results: Array<PartialData<IdAndName<string, string>>> =
                    [];

                const categoryTags = tags.tags.filter(
                    (t) => t.tagCategoryId === category.id
                );
                categoryTags.forEach((tag) => {
                    const idAndName: IdAndName<string, string> = {
                        id: tag.id,
                        name: tag.name,
                    };
                    results.push({
                        partialId: `${category.name}-${tag.name}`,
                        expandedValue: idAndName,
                    });
                });
                return results;
            },
            // NOTE: comparison does not play any role, because category_ columns are not shown, but only used in search
            compare: (_a, _b) => 0,
            asString: (t: IdAndName<string, string>) => t.name,
            customFilterTransformer: {
                getValue: (idAndName) => idAndName,
                asString: tagNameLookup,
            },
        };

        extractors.push(extractor);
        expanders.push(expander);
    });

    return { extractors, expanders };
}

function buildAssigneeTagCategoryExtractorExpanderForNonGroupedTagsWithIds<
    TAG extends AbstractTag,
    CAT extends AbstractTagCategory,
>(
    allTags: TagLookupData<TAG, CAT>
): ExtractorExpander<TagListProps, IdAndName<string, string>> {
    const categoryNameLookup = createCategoryNameLookupFn(allTags);

    const result: ExtractorExpander<TagListProps, IdAndName<string, string>> = {
        extractorId: ASSIGNEE_TAG_CATEGORIES_ID,
        expand: (tags: TagListProps) => {
            const results: Array<PartialData<IdAndName<string, string>>> = [];
            const sortedTagsByCategory = getTagsByCategory(
                tags.tags,
                tags.tagCategories
            );
            sortedTagsByCategory.forEach(({ category, tags }) => {
                tags.forEach((tag) => {
                    // We are only interested in category names.
                    // However, a CR tagged with N tags in the same
                    // category should count N times for this
                    // category, not 1 time
                    const idAndName: IdAndName<string, string> = {
                        id: category.id,
                        name: category.name,
                    };
                    results.push({
                        partialId: `${category.name}-${tag.name}`,
                        expandedValue: idAndName,
                    });
                });
            });
            return results;
        },
        compare: Cmp.undefinedLast(
            Cmp.map((idAndName) => idAndName.name, Cmp.string())
        ),
        asString: categoryNameLookup,
        customFilterTransformer: {
            getValue: (idAndName: IdAndName<string, string>) => idAndName,
            asString: categoryNameLookup,
        },
    };
    return result;
}

interface TagCategoryExtractorsAndExpandersInnerTable<T> {
    extractors: Array<
        DataExtractor<T, UserTagView[], IdAndName<string, string>>
    >;
    expanders: Array<
        ExtractorExpander<UserTagView[], IdAndName<string, string>>
    >;
}

function buildAssigneeCategoryExtractorsAndExpandersFromTagsAndFilterInnerTable<
    TAG extends UserTagView,
    CAT extends AbstractTagCategory,
    ROW extends AssignmentRow,
>(
    allTags: TagLookupData<TAG, CAT>,
    currentFilter: SimpleAndFilter | null
): TagCategoryExtractorsAndExpandersInnerTable<ROW> {
    const tagNameLookup = createTagNameLookupFn(allTags);
    const tagsById = new Map(
        allTags.content.map(({ resource }) => [resource.id, resource])
    );

    const extractors: Array<
        DataExtractor<ROW, UserTagView[], IdAndName<string, string>>
    > = [];
    const expanders: Array<
        ExtractorExpander<UserTagView[], IdAndName<string, string>>
    > = [];

    const allCategories: AbstractTagCategory[] = allTags.linkedResources.map(
        ({ id, name }) => ({ id, name })
    );
    // In case if one of the categories was removed, but it's still referred in the filter, we want to "resurrect" it by decoding
    // id and name from extractorId, so we can build an extractor and expander.
    if (currentFilter) {
        currentFilter.filters
            .map((f) => f.extractorId)
            .filter(ASSIGNEE_CATEGORY_METHODS.isCategoryExtractorId)
            .forEach((categoryExtractorId) => {
                const { id, name } =
                    ASSIGNEE_CATEGORY_METHODS.extractorIdToAbstractTagCategory(
                        categoryExtractorId
                    );
                const isFound = !!allCategories.find((c) => c.id === id);
                if (!isFound) {
                    allCategories.push({ id, name });
                }
            });
    }

    allCategories.forEach((category) => {
        const extractorId: string =
            ASSIGNEE_CATEGORY_METHODS.abstractTagCategoryToExtractorId({
                id: category.id,
                name: category.name,
            });

        const extractor: DataExtractor<
            ROW,
            UserTagView[],
            IdAndName<string, string>
        > = {
            id: extractorId,
            label:
                t("Control_Management_AssigneeTagCategory") +
                ": " +
                category.name,
            getValue: (row) =>
                row.tagIds.flatMap((tagId) => tagsById.get(tagId) ?? []),
            // NOTE: comparison does not play any role, because category_ columns are not shown, but only used in search
            compare: (_a, _b) => 0,
            asString: (_row) => {
                throw new Error(
                    `Unexpected call of asString() on ${extractorId} extractor.`
                );
            },
            customFilterTransformer: {
                getValue: (_row) => {
                    throw new Error(
                        `Unexpected call of customFilterTransformer.getValue() on ${extractorId} extractor.`
                    );
                },
                asString: tagNameLookup,
            },
        };

        const expander: ExtractorExpander<
            UserTagView[],
            IdAndName<string, string>
        > = {
            extractorId,
            expand: (tags: UserTagView[]) => {
                const results: Array<PartialData<IdAndName<string, string>>> =
                    [];

                const categoryTags = tags.filter(
                    (t) => t.tagCategoryId === category.id
                );
                categoryTags.forEach((tag) => {
                    const idAndName: IdAndName<string, string> = {
                        id: tag.id,
                        name: tag.name,
                    };
                    results.push({
                        partialId: `${category.name}-${tag.name}`,
                        expandedValue: idAndName,
                    });
                });
                return results;
            },
            // NOTE: comparison does not play any role, because category_ columns are not shown, but only used in search
            compare: (_a, _b) => 0,
            asString: (t: IdAndName<string, string>) => t.name,
            customFilterTransformer: {
                getValue: (idAndName) => idAndName,
                asString: tagNameLookup,
            },
        };

        extractors.push(extractor);
        expanders.push(expander);
    });

    return { extractors, expanders };
}

export {
    ASSIGNEE_CATEGORY_METHODS,
    buildAssigneeCategoryExtractorsAndExpandersFromTagsAndFilter,
    buildAssigneeCategoryExtractorsAndExpandersFromTagsAndFilterInnerTable,
    ASSIGNEE_CATEGORIES_ID,
    ASSIGNEE_TAG_CATEGORIES_ID,
    buildAssigneeTagCategoryExtractorExpanderForNonGroupedTagsWithIds,
};
